import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

interface CreateCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateCollection: (name: string) => void;
}

const CreateCollectionDialog = ({
  open,
  onOpenChange,
  onCreateCollection,
}: CreateCollectionDialogProps) => {
  const [collectionName, setCollectionName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (collectionName.trim()) {
      onCreateCollection(collectionName);
      setCollectionName("");
      onOpenChange(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const diff = currentY - startY;
    if (diff > 100) {
      // If dragged down more than 100px, close the dialog
      onOpenChange(false);
    }
  };

  const translateY = isDragging ? Math.max(0, currentY - startY) : 0;

  if (!open) return null;

  return (
    <>
      {/* Mobile Bottom Sheet */}
      <div className="md:hidden">
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
          onClick={() => onOpenChange(false)}
        />
        <div 
          className={cn(
            "fixed bottom-0 left-0 right-0 z-50",
            "bg-white rounded-t-2xl shadow-lg",
            "transition-transform duration-300 ease-in-out"
          )}
          style={{
            transform: `translateY(${translateY}px)`,
            maxHeight: '90vh',
          }}
        >
          {/* Drag Handle */}
          <div 
            className="w-full flex flex-col items-center cursor-grab active:cursor-grabbing pt-2"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mb-4" />
          </div>

          <div className="px-4 pb-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Create New Collection</h2>
              <button 
                onClick={() => onOpenChange(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-6">
              Give your collection a name to help organize your tabs.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label 
                  htmlFor="name-mobile" 
                  className="text-sm font-medium"
                >
                  Collection Name
                </Label>
                <Input
                  id="name-mobile"
                  value={collectionName}
                  onChange={(e) => setCollectionName(e.target.value)}
                  placeholder="e.g., Work, Research, Shopping"
                  className="w-full focus:ring-2 focus:ring-hintword-purple focus:border-transparent"
                  autoFocus
                />
              </div>

              <div className="flex flex-col gap-3">
                <Button 
                  type="submit" 
                  className={cn(
                    "w-full",
                    "bg-hintword-pink hover:bg-hintword-pink/90"
                  )}
                  disabled={!collectionName.trim()}
                >
                  Create Collection
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => onOpenChange(false)}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Desktop Dialog */}
      <div className="hidden md:block">
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-xl">Create New Collection</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Give your collection a name to help organize your tabs.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label 
                    htmlFor="name-desktop" 
                    className="text-sm font-medium"
                  >
                    Collection Name
                  </Label>
                  <Input
                    id="name-desktop"
                    value={collectionName}
                    onChange={(e) => setCollectionName(e.target.value)}
                    placeholder="e.g., Work, Research, Shopping"
                    className={cn(
                      "w-full",
                      "focus:ring-2 focus:ring-hintword-purple focus:border-transparent"
                    )}
                    autoFocus
                  />
                </div>
              </div>
              <DialogFooter className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => onOpenChange(false)}
                  type="button"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-hintword-pink hover:bg-hintword-pink/90"
                  disabled={!collectionName.trim()}
                >
                  Create Collection
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default CreateCollectionDialog;
